import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmationDialogType, DeleteConfirmationOptions, NewsType, NotificationType, SectionType, SocketEvents, TagType } from '@core/enums';

import { FormService, Logger } from '@core/services';
import { GroupEntity, GroupNewsEntity, NewsEntity, PaginatedEntity, Pagination, UserEntity } from '@core/entities';
import { UseMyContent, UseNews, UseTag, UseTask } from '@core/usecases';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, scrollToTop, stripHtml, updateObjectProperties } from '@core/utils';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { HotToastService } from '@ngxpert/hot-toast';
import { DialogService } from '@ngneat/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DropDownItem } from '@core/types/Dropdown.type';
import { ActivatedRoute, Router } from '@angular/router';
import { PERMISSIONS, PermissionService } from '@auth';
import { instanceToPlain } from 'class-transformer';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent, SingleInputCreateComponent } from '@web/_shared/dialogs';
import { SocketIoService } from '@core/socket-io';
import { BreadcrumbItem, FilterQuery } from '@core/types';
import { ButtonComponent } from '@shared/components/ui';

const log = new Logger('News Composition');

export class NewsComposition extends FormService {
  breadcrumbs: BreadcrumbItem[] = [];
  currentTabRoute = '';
  currentDate = new Date();
  selectedImage: File | string;
  currentNews: NewsEntity;
  newsList: NewsEntity[] = [];
  groupNewsList: GroupNewsEntity[] = [];

  // These below 2 properties `originalRecord` and `updatedRecord` are used to show admin the changes requested by the user
  originalRecord: NewsEntity;
  updatedRecord: NewsEntity;

  groups: Partial<GroupEntity>[] = [];
  users: Partial<UserEntity>[] = [];
  tags: DropDownItem[] = [];
  tabs: { label: string; displayName?: string; routerLink?: string; count?: number }[] = [];
  newsTypes;
  pagination: Pagination = new PaginatedEntity().pagination;

  protected readonly _bottomSheetService: NgxBottomSheetService;
  protected readonly _dialogService: DialogService;
  protected readonly _translateService = inject(TranslateService);
  protected readonly _router = inject(Router);
  protected readonly _activatedRoute = inject(ActivatedRoute);
  protected readonly _toast = inject(HotToastService);
  protected readonly _permissionService = inject(PermissionService);
  protected readonly _socketService: SocketIoService = inject(SocketIoService);

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _useTask = new UseTask();
  private readonly _useTags = new UseTag();
  private readonly _useNews = new UseNews();
  private readonly _useMyContent = new UseMyContent();

  constructor() {
    super();

    this.isLoading = true;
    this.newsTypes = EnumToKeyValueArray(NewsType, this._translateService);
    if (!isMobileView()) {
      this._dialogService = inject(DialogService);
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
      this.currentNews = this._bottomSheetService.currentSheetData;
    }

    this._socketService
      .fromEvent(SocketEvents.GetNotification)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: NotificationType) => {
          if (data === NotificationType.News) {
            log.info('Updating News List');
            this._reloadCurrentRoute();
          }
        },
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- Getters & Get Methods --------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get isUserAdmin() {
    return this._permissionService.isAdmin();
  }

  get newsCreatPermission(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_NEWS);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ Action Methods -----------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const form = {
      ...this.form.value,
      audience: this.form.get('audience')?.value,
      groups: this.form.get('groups')?.value,
    } as NewsEntity;
    form.createdBy = GetUserId();
    form.teamId = GetTeamId();
    form.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    form.author = GetUserId();
    form.text = form.headline;
    form.highlighted = form.isHighlighted;

    const newsData = new NewsEntity(form);
    const transformedData = this.removeEventFields(this.removeNullObjects(instanceToPlain(newsData)));

    // This line added as multiple groups are not supported yet
    transformedData.groups = transformedData.groups ? [transformedData.groups] : null;

    const formData = this.jsonToFromData(transformedData, ['news_tags', 'groups']);

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }
    if (form.audience !== NewsType.Group) {
      formData.delete('groups');
    }

    formData.append('tags', null);
    formData.append('attachment', null);
    formData.delete('is_highlighted');

    const service$ = this.currentNews && this.currentNews?.id ? this._useNews.updateNews(this.currentNews?.id, formData) : this._useNews.createNews(formData);
    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translateService.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this.currentNews && this.currentNews?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translateService.instant(message));
          }

          if (!isMobileView()) {
            this._dialogService.closeAll();
          } else {
            this._bottomSheetService.closeAll();
          }
          this._socketService.emit(SocketEvents.SendNotification, NotificationType.News);
          this._reloadCurrentRoute();
        } else {
          this._toast.error(this._translateService.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating News');
      });
  }

  //
  deleteNews(news: NewsEntity) {
    if (!news.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: news.title,
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useNews.deleteNews(news.id)).then(() => {
                  this._toast.success(this._translateService.instant('Delete Successful'));
                  this._bottomSheetService.close();
                  this._dialogService?.closeAll();
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.News);
                  // this._reloadCurrentRoute();
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: news.title,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useNews.deleteNews(news.id)).then(() => {
              this._toast.success(this._translateService.instant('Delete Successful'));
              this._socketService.emit(SocketEvents.SendNotification, NotificationType.News);
              this._reloadCurrentRoute();
              this._dialogService.closeAll();
            });
          }
        },
        error: (err) => {
          this._toast.error(err?.error?.message || this._translateService.instant('Something went wrong'));
        },
      });
    }
  }

  denyNews(news: NewsEntity, btnRef?: ButtonComponent) {
    btnRef?.showLoading();
    this._dialogService
      .open(SingleInputCreateComponent, {
        closeButton: false,
        data: {
          title: this._translateService.instant('Are you sure you want to unapprove this') + ' ' + this._translateService.instant('News'),
          label: this._translateService.instant('Message'),
          inputType: 'textArea',
        },
      })
      .afterClosed$.subscribe({
        next: (reason) => {
          if (reason) {
            this._useNews
              .denyNews(news.id, reason)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toast.success(this._translateService.instant('Rejected Successfully'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.News);
                  btnRef?.showIdle();
                  // this._reloadCurrentRoute();
                },
                error: (err) => {
                  this._toast.error(err?.error?.message || this._translateService.instant('Something went wrong'));
                  btnRef?.showError();
                },
              });
          } else {
            btnRef?.showIdle();
          }
        },
      });
  }

  approveNewsUpdate(news: NewsEntity, isUpdate = false, btnRef?: ButtonComponent) {
    btnRef?.showLoading();
    this._dialogService
      .open(ConfirmationDialogComponent, {
        closeButton: false,
        data: {
          category: SectionType.NEWS,
          type: ConfirmationDialogType.APPROVE,
        },
      })
      .afterClosed$.subscribe({
        next: (confirmed) => {
          if (confirmed) {
            const service$ = isUpdate ? this._useNews.acceptNewsUpdateByAdmin(news.id) : this._useNews.acceptNewsByAdmin(news.id);

            service$.pipe(untilDestroyed(this)).subscribe({
              next: () => {
                this._toast.success(this._translateService.instant('Approved Successfully'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.News);
                btnRef?.showIdle();
                // this._reloadCurrentRoute();
              },
              error: (err) => {
                this._toast.error(err?.error?.message || this._translateService.instant('Something went wrong'));
                btnRef?.showError();
              },
            });
          } else {
            btnRef?.showIdle();
          }
        },
      });
  }

  _initDataForForm() {
    this._initNewsForm();
    this._fetchTags();
  }

  close() {
    if (!isMobileView()) {
      this._dialogService.closeAll();
    } else {
      this._bottomSheetService.close();
    }
  }

  changePagination(pagination: Pagination) {
    this.pagination = pagination;
    this.isLoading = true;
    this._fetchAllNews();
  }

  onFilterChange(filter: FilterQuery) {
    this.pagination.page = 1;
    this.isLoading = true;
    this._fetchAllNews(filter);
  }

  onUpdateRecordTabSelection(tabName: 'current' | 'update' | string) {
    switch (tabName) {
      case 'current':
        this.currentNews = this.originalRecord;
        break;
      case 'update':
        this.currentNews = this.updatedRecord;
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _patchFormValues(news: NewsEntity) {
    const tags = news.newsTags ? news.newsTags.map((tag) => tag.tagName) : [];
    const groupIds = news.groups ? (news.groups as GroupEntity[]).map((group: GroupEntity) => group.id) : [];
    this.form.patchValue({
      title: stripHtml(news.title),
      audience: news.audience,
      newsTags: tags,
      groups: groupIds[0],
      headline: stripHtml(news.headline),
      showGuestList: news.showGuestList,
      isHighlighted: news.isHighlighted,
      image: news.imageUrl ? news.imageUrl : null,
    });
  }

  protected _fetchTeamUsersAndGroups() {
    this.call(this._useTask.getTeamUsersAndGroups()).then((response) => {
      this.groups = this.removeNullObjects(response.groups) as Partial<GroupEntity>[];
    });
  }

  protected _fetchAllNews(filters?: FilterQuery): void {
    if (filters) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      Object.keys(filters).forEach((key) => (filters[key] === null || filters[key] === 0 || filters[key] === 'all') && delete filters[key]);
    }

    this._useNews
      .getAllNews(this.pagination, filters)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (newsList) => {
          this.newsList = newsList.news;
          this.pagination = newsList.pagination;
          this.isLoading = false;
          scrollToTop();

          this._useNews.markNewsAsRead().pipe(untilDestroyed(this)).subscribe();
        },
        error: (error) => {
          this._toast.error(error?.error?.message || this._translateService.instant('Something went wrong'));
          this.isLoading = false;
        },
      });
  }

  protected _fetchAllGroupNews(): void {
    this.isLoading = true;
    this._useNews
      .getAllGroupNews(this.pagination)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupNews) => {
          this.pagination = groupNews.pagination;
          this.groupNewsList = groupNews.groupNews;
          this.isLoading = false;
          scrollToTop();
        },
      });
  }

  protected _fetchNewsById(newsId: number, isEdit: boolean) {
    this.call(this._useNews.getNewsById(newsId)).then((response) => {
      if (this.currentNews !== response) {
        this.originalRecord = response;
        this.currentNews = this.originalRecord;
        if (this.originalRecord.updatedRecord) {
          this.updatedRecord = updateObjectProperties(this.originalRecord, this.originalRecord.updatedRecord);
        }
        if (isEdit) {
          this._patchFormValues(response);
        }
      } else if (isEdit) {
        this._patchFormValues(response);
      }
    });
  }

  // write a function to convert comma separated string to array of strings
  private _initNewsForm() {
    this.form = this._formBuilder.group({
      title: ['', [Validators.required]],
      audience: ['', [Validators.required]],
      newsTags: [[]],
      headline: ['', [Validators.required]],
      showGuestList: [false],
      isHighlighted: [false],
      image: [null],
      groups: [''],
    });
  }

  private _fetchTags() {
    this._useTags
      .getAllTagsByType(TagType.NEWS)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.tags = res.map((tag) => ({ label: tag.tagName, value: tag.tagName }));
          }
        },
      });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
